import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  Divider,
} from '@mui/material';
import {
  School,
  EmojiEvents,
  Groups,
  Science,
  History,
  AutoStories,
  Psychology,
  Lightbulb,
} from '@mui/icons-material';

const About = () => {
  const theme = useTheme();

  const highlights = [
    {
      icon: <History sx={{ fontSize: 40 }} />,
      title: "Rich History",
      description: "Established in 1992, we were pioneers in computer education with PGDCA (1984) and MCA (1988) programs."
    },
    {
      icon: <AutoStories sx={{ fontSize: 40 }} />,
      title: "Comprehensive Programs",
      description: "Offering B.E. in CSE (1990), B.E. in IT (2000), M.E. in CSE (2006), and Ph.D. in Computer Science (2009)."
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Growing Community",
      description: "Started with 33 students in 1992, now nurturing tech minds for the emerging technical world."
    },
    {
      icon: <Science sx={{ fontSize: 40 }} />,
      title: "Research Focus",
      description: "Expanding with technological development to meet society's and industry's demands."
    }
  ];

  const visionMission = [
    {
      icon: <Lightbulb sx={{ fontSize: 40 }} />,
      title: "Vision",
      content: "To be a leader in providing excellent educational, research and techno-entrepreneurial resources in the field of Computer Science and Engineering to meet the existing, future and interdisciplinary needs of industry and society."
    },
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      title: "Mission",
      content: [
        "To provide quality education through state-of-the-art teaching-learning methods to produce competent graduates and post graduates in the field of Computer Science, IT and Applications.",
        "To create facilities and resources to discover and disseminate knowledge to students for the advancement of society and industry.",
        "To partner and collaborate with industry, government and R&D institutions to pace with latest research & sustainable technologies and ensure quality assurance.",
        "To impart innovative and entrepreneurial outlook, values, ethics and vision to students to make them responsible professionals and global citizens."
      ]
    }
  ];

  return (
    <Box sx={{ 
      py: { xs: 4, md: 8 },
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
      minHeight: '100vh'
    }}>
      <Container maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom sx={{ 
          mb: 8,
          color: 'primary.main',
          fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.5rem' },
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            background: 'linear-gradient(90deg, #1a237e 0%, #c62828 100%)',
            borderRadius: 2
          }
        }}>
          About Us
        </Typography>

        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" gutterBottom sx={{ 
            color: 'primary.main',
            fontWeight: 600,
            mb: 3
          }}>
            Department of Computer Science & Engineering
          </Typography>
          <Typography variant="body1" paragraph sx={{ 
            color: 'text.secondary',
            fontSize: '1.1rem',
            lineHeight: 1.8
          }}>
            MBM University was one of the earliest institutes in the country to provide education opportunities to students in the computing domain through PGDCA (Post Graduate Diploma in Computer Applications) and MCA (Master of Computer Applications) programmes in the year 1984 and 1988 respectively. Later, the Department of Computer Science & Engineering was established as a standalone department in the year 1992 after the introduction of Bachelor of Engineering (Computer Science & Engineering) programme.
          </Typography>
          <Typography variant="body1" paragraph sx={{ 
            color: 'text.secondary',
            fontSize: '1.1rem',
            lineHeight: 1.8
          }}>
            The Department of Computer Science & Engineering was established in the year 1992 with the intake of 33 students in B.E. Computer Science & Engineering. B.E. in Computer Science & Engineering (4 Years Degree Course) was started 1990 followed by B.E. in Information Technology (SFS – 4 Years Degree Course) in 2000. Later, M.E. with specialization in Computer Science & Engineering was started in 2006 and finally, Ph.D. in Computer Science was started in 2009.
          </Typography>
          <Typography variant="body1" paragraph sx={{ 
            color: 'text.secondary',
            fontSize: '1.1rem',
            lineHeight: 1.8
          }}>
            Progressively, the Department has grown up with the pace of technological development and started more courses as required by demand of society, time and industry. With time the department has expanded in limits and with this increase has increased the tech minds who will be holding the reins of the emerging technical world.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {highlights.map((highlight, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    color: 'primary.main'
                  }}>
                    {highlight.icon}
                    <Typography variant="h6" sx={{ ml: 2, fontWeight: 700 }}>
                      {highlight.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    {highlight.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {visionMission.map((item, index) => (
            <Grid item xs={12} key={index}>
              <Card sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    color: 'primary.main'
                  }}>
                    {item.icon}
                    <Typography variant="h5" sx={{ ml: 2, fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                  </Box>
                  {Array.isArray(item.content) ? (
                    <Box component="ul" sx={{ pl: 2 }}>
                      {item.content.map((point, idx) => (
                        <Typography 
                          component="li" 
                          variant="body1" 
                          color="text.secondary" 
                          sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.8 }}
                          key={idx}
                        >
                          {point}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                      {item.content}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h4" align="center" sx={{ 
          mt: 8,
          mb: 6,
          color: 'primary.main',
          fontWeight: 700
        }}>
          Our Programs
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 3
                }}>
                  Computer Science & Engineering (CSE)
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  Our flagship program focuses on core computer science concepts, software development, and system design. Students learn programming languages, data structures, algorithms, database management, and computer networks.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  The curriculum emphasizes practical implementation through projects, internships, and industry collaborations, preparing students for roles in software development, system architecture, and research.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 3
                }}>
                  Information Technology (IT)
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  The IT program focuses on the application of technology in business and industry. Students learn about information systems, web technologies, cloud computing, and enterprise software solutions.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  With a strong emphasis on practical skills and industry trends, graduates are well-prepared for careers in IT consulting, system administration, and enterprise software development.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 3
                }}>
                  Artificial Intelligence & Data Science (AI&DS)
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  Our newest program combines AI, machine learning, and data science to prepare students for the future of technology. The curriculum covers machine learning algorithms, deep learning, big data analytics, and AI applications.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  Students work on real-world projects involving data analysis, predictive modeling, and AI system development, preparing them for roles in AI research, data science, and machine learning engineering.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About; 